/**
 * Shared format validators for the admin portal. Every form that
 * collects an email or phone must go through these helpers so
 * rejection messages stay consistent and the portal stops silently
 * submitting garbage at the API.
 *
 * All validators return the same shape: `{ ok, error? }`. Callers
 * block submit on `!ok` and render `error` next to the field.
 */

export interface ValidationResult {
  ok: boolean;
  error?: string;
}

// Matches the common "local@domain.tld" shape without drifting into
// the full RFC 5322 minefield. Rejects leading/trailing whitespace,
// multiple @, empty local or domain parts, and TLDs shorter than 2
// chars. Allows subdomains, +tags, and internationalised-looking
// Latin/ASCII characters. If we need true i18n email support later,
// swap to a library.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** Validate an email address. Empty string is allowed - callers that
 *  require the field must check non-empty separately. */
export function validateEmail(raw: string, opts?: { required?: boolean }): ValidationResult {
  const v = raw.trim();
  if (v === "") {
    if (opts?.required) return { ok: false, error: "Email is required." };
    return { ok: true };
  }
  if (v.length > 254) return { ok: false, error: "Email is too long (max 254 characters)." };
  if (!EMAIL_RE.test(v))
    return { ok: false, error: "Enter a valid email address (name@example.com)." };
  return { ok: true };
}

// US/CA phone: NANP - exactly 10 digits, or 11 with leading 1. Any
// mix of space / dash / dot / parens / plus is allowed in the input;
// strip to digits before counting. If the product ships beyond North
// America we'll want libphonenumber; flag explicitly rather than
// pretending a regex covers the world.
const PHONE_DIGITS_RE = /\d/g;
const PHONE_CHARSET_RE = /^[\d\s().+-]+$/;

/** Validate a phone number against NANP (US + Canada). `{ok:true}` on
 *  empty unless required. */
export function validatePhone(raw: string, opts?: { required?: boolean }): ValidationResult {
  const v = raw.trim();
  if (v === "") {
    if (opts?.required) return { ok: false, error: "Phone is required." };
    return { ok: true };
  }
  if (!PHONE_CHARSET_RE.test(v)) {
    return { ok: false, error: "Phone number contains invalid characters." };
  }
  const digits = (v.match(PHONE_DIGITS_RE) ?? []).join("");
  // NANP: 10 digits, or 11 with leading 1 (country code).
  if (digits.length === 10) return { ok: true };
  if (digits.length === 11 && digits.startsWith("1")) return { ok: true };
  return {
    ok: false,
    error: "Enter a valid US/Canada phone number (10 digits, or 11 with leading 1).",
  };
}

export type SupportedCountry = "US" | "CA";

// 5-digit ZIP or ZIP+4.
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;
// Canadian postal code: A1A 1A1 (space optional, case-insensitive).
// Excludes letters D, F, I, O, Q, U per Canada Post rules.
const CA_POSTAL_RE = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ] ?\d[ABCEGHJKLMNPRSTVWXYZ]$/i;

/** Validate postal / ZIP against the selected country. */
export function validatePostalCode(
  raw: string,
  country: SupportedCountry,
  opts?: { required?: boolean },
): ValidationResult {
  const v = raw.trim();
  if (v === "") {
    if (opts?.required) return { ok: false, error: "Postal / ZIP is required." };
    return { ok: true };
  }
  if (country === "US" && !US_ZIP_RE.test(v)) {
    return { ok: false, error: "Enter a valid US ZIP (12345 or 12345-6789)." };
  }
  if (country === "CA" && !CA_POSTAL_RE.test(v)) {
    return { ok: false, error: "Enter a valid Canadian postal code (A1A 1A1)." };
  }
  return { ok: true };
}

/** US state / territory 2-letter codes. */
export const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
] as const;

/** Canadian province / territory 2-letter codes. */
export const CA_PROVINCES = [
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NT",
  "NU",
  "ON",
  "PE",
  "QC",
  "SK",
  "YT",
] as const;

export function statesForCountry(country: SupportedCountry): readonly string[] {
  return country === "US" ? US_STATES : CA_PROVINCES;
}

/** Validate a state / province against the selected country. */
export function validateState(
  raw: string,
  country: SupportedCountry,
  opts?: { required?: boolean },
): ValidationResult {
  const v = raw.trim().toUpperCase();
  if (v === "") {
    if (opts?.required) return { ok: false, error: "State / province is required." };
    return { ok: true };
  }
  const allowed: readonly string[] = statesForCountry(country);
  if (!allowed.includes(v)) {
    return {
      ok: false,
      error:
        country === "US" ? "Not a valid US state code." : "Not a valid Canadian province code.",
    };
  }
  return { ok: true };
}

/** Trivial convenience for "show red + message under a field" - pass
 *  a validation result and render the error if any. */
export function fieldError(r: ValidationResult | undefined): string | undefined {
  return r?.ok === false ? r.error : undefined;
}
